import { useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Plane, Search, Trash2 } from 'lucide-react';
import { useApp } from '../../data/AppProvider';
import { keys, useTrips } from '../../data/hooks';
import { cx } from '../ui';

/** Recent conversations with Elio, searchable. Lives in the sidebar on desktop and a sheet on phones. */
export function ConversationNav({ onNavigate }: { onNavigate?: () => void }) {
  const { data, session } = useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const trips = useTrips();
  const conversations = useQuery({ queryKey: keys.conversations, queryFn: () => data.listConversations(), enabled: Boolean(session) && data.kind !== 'demo' });
  const [q, setQ] = useState('');
  // Each conversation keeps its own subject; the plane icon shows it is attached to a trip.
  const titleOf = (c: { trip_id: string | null; title: string }) => (c.title && c.title !== 'Conversation' ? c.title : c.trip_id ? (trips.data ?? []).find((t) => t.id === c.trip_id)?.title ?? c.title : c.title);
  const items = useMemo(() => {
    const all = conversations.data ?? [];
    const needle = q.trim().toLowerCase();
    return needle ? all.filter((c) => titleOf(c).toLowerCase().includes(needle)) : all;
  }, [conversations.data, q, trips.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if ((conversations.data ?? []).length === 0) return null;

  const remove = async (id: string) => {
    if (!window.confirm('Delete this conversation? Changes you confirmed stay in your trips.')) return;
    await data.deleteConversation(id);
    await qc.invalidateQueries({ queryKey: keys.conversations });
    if (id === conversationId) navigate('/');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted">Recent</p>
      {(conversations.data ?? []).length > 6 && (
        <label className="relative mx-1 mb-2 block">
          <span className="sr-only">Search conversations</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="h-10 w-full rounded-full border border-line bg-white pl-9 pr-3 text-sm focus:border-forest-500 focus:outline-none" />
        </label>
      )}
      <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
        {items.map((c) => (
          <li key={c.id} className="group relative">
            <NavLink to={`/c/${c.id}`} onClick={onNavigate}
              className={({ isActive }) => cx('flex min-h-10 items-center gap-2.5 rounded-xl px-3 py-1.5 pr-9 text-sm transition-colors', isActive ? 'bg-white font-semibold text-ink shadow-[var(--shadow-card)]' : 'text-ink/80 hover:bg-white/70')}>
              {c.trip_id ? <Plane className="size-4 shrink-0 text-ocean-600" aria-label={`About ${(trips.data ?? []).find((t) => t.id === c.trip_id)?.title ?? 'a trip'}`} /> : <MessageSquare className="size-4 shrink-0 text-muted" aria-hidden />}
              <span className="truncate">{titleOf(c)}</span>
            </NavLink>
            <button type="button" aria-label={`Delete conversation ${titleOf(c)}`} onClick={() => void remove(c.id)}
              className="absolute right-1 top-1/2 hidden size-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted hover:bg-ivory hover:text-danger focus-visible:flex group-hover:flex"><Trash2 className="size-3.5" /></button>
          </li>
        ))}
        {items.length === 0 && <li className="px-3 py-2 text-sm text-muted">No matches.</li>}
      </ul>
    </div>
  );
}
